import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { getProduct, CART_EXTRA } from "../config";

const QUESTIONS = [
  {
    q: "Aujourd'hui, votre dentifrice contient-il du fluor ?",
    answers: ["Oui, comme la plupart", "Non, j'ai déjà changé", "Aucune idée"],
    // Single explanation regardless of answer.
    explain: () => "Le fluor protège l'émail en le durcissant en surface. La nano-hydroxyapatite va plus loin : elle apporte le minéral dont l'émail est fait à 97 %, et contribue à le reminéraliser. Deux approches — nous avons choisi la seconde, désormais soutenue par des essais cliniques.",
  },
  {
    q: "Qu'est-ce qui vous retient le plus de changer de dentifrice ?",
    answers: ["J'ai peur que ce soit moins efficace", "Le prix", "L'habitude, tout simplement"],
    explain: (i) => [
      "Normal. C'est pourquoi nous publions les études : la nano-hydroxyapatite a été comparée au fluor sur 18 mois sans se montrer inférieure. Tout est sur notre page Science.",
      "Un kit = 62 brossages soit 0,60€ seulement par brossage. Sans aucun surdosage ni gaspillage",
      "Le geste ne change pas : vous croquez, vous brossez, vous rincez. Comptez trois jours pour que ça devienne un réflexe.",
    ][i],
  },
  {
    q: "Où en êtes-vous dans votre routine ?",
    answers: ["Je suis capable de tout changer d'un coup", "Je préfère tester un produit d'abord", "Je cherche surtout un produit pour voyager"],
    explain: null,
  },
];

const RESULTS = [
  { productId: "kit", title: "VOTRE PROFIL : LE GRAND SAUT.", text: "Le kit complet réunit dentifrice, bain de bouche et brosse — tout ce qu'il faut pour changer de routine en une décision.", cta: "Découvrir le kit — 45 €" },
  { productId: "pastilles", title: "VOTRE PROFIL : LE PRUDENT.", text: "Commencez par les pastilles dentifrice — l'essentiel de CRUSH, sans engagement.", cta: "Essayer les pastilles — 32 €" },
  { productId: "kit", title: "VOTRE PROFIL : LE NOMADE.", text: "Solide, compact, zéro liquide : le kit vous suit partout, cabine comprise.", cta: "Découvrir le kit — 45 €" },
];

const ease = [0.22, 1, 0.36, 1];

export const Quiz = ({ open, onClose }) => {
  const { addItem, setOpen: setCartOpen } = useCart();
  const [step, setStep] = useState(0);        // 0..2 questions, 3 = result
  const [q3, setQ3] = useState(0);
  const [reveal, setReveal] = useState(null); // explanation text for current question

  const reset = () => { setStep(0); setQ3(0); setReveal(null); };
  const close = () => { reset(); onClose(); };

  const choose = (qi, ai) => {
    if (qi === 2) setQ3(ai);
    const ex = QUESTIONS[qi].explain ? QUESTIONS[qi].explain(ai) : null;
    if (ex) {
      setReveal(ex);
    } else {
      setStep(qi + 1);
    }
  };

  const next = () => { setReveal(null); setStep((s) => s + 1); };

  const result = RESULTS[q3];
  const progress = step < 3 ? ((step + (reveal ? 0.5 : 0)) / 3) * 100 : 100;

  const addRecommended = () => {
    const rp = result.productId;
    addItem(rp, getProduct(rp).variants?.length ? getProduct(rp).variants[0] : null);
    close();
    setCartOpen(true);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="quiz-overlay"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          style={{ position: "fixed", inset: 0, zIndex: 120, background: "var(--bg)", display: "flex", flexDirection: "column", overflowY: "auto" }}
        >
          {/* progress + close */}
          <div style={{ position: "sticky", top: 0, background: "var(--bg)", padding: "18px 20px", borderBottom: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <span className="display" style={{ letterSpacing: "0.1em", fontSize: 14, color: "var(--text-muted)" }}>LE TEST CRUSH</span>
              <button onClick={close} data-testid="quiz-close" aria-label="Fermer" style={{ background: "none", border: "none", color: "var(--text)", cursor: "pointer" }}><X /></button>
            </div>
            <div style={{ height: 3, background: "var(--border)", borderRadius: 4 }}>
              <motion.div animate={{ width: `${progress}%` }} transition={{ duration: 0.4, ease }} style={{ height: 3, background: "var(--accent)", borderRadius: 4 }} />
            </div>
          </div>

          <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
            <div style={{ width: "100%", maxWidth: 620 }}>
              <AnimatePresence mode="wait">
                {step < 3 && !reveal && (
                  <motion.div key={`q${step}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5, ease }}>
                    <span className="eyebrow" style={{ color: "var(--accent-light)" }}>{`Question ${step + 1} / 3`}</span>
                    <h2 className="title-xl" style={{ fontSize: "clamp(26px,5vw,40px)", margin: "16px 0 32px" }}>{QUESTIONS[step].q}</h2>
                    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                      {QUESTIONS[step].answers.map((a, ai) => (
                        <button key={ai} data-testid={`quiz-q${step}-a${ai}`} onClick={() => choose(step, ai)} className="btn btn-secondary"
                          style={{ justifyContent: "flex-start", textTransform: "none", letterSpacing: "normal", fontFamily: "var(--body)", padding: "18px 22px", fontSize: 16 }}>{a}</button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {step < 3 && reveal && (
                  <motion.div key={`e${step}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.5, ease }} data-testid="quiz-explain">
                    <span className="eyebrow" style={{ color: "var(--accent-light)" }}>Ce qu'il faut savoir</span>
                    <p style={{ fontSize: 20, lineHeight: 1.6, margin: "20px 0 32px" }}>{reveal}</p>
                    <button className="btn btn-primary" data-testid="quiz-continue" onClick={next}>Continuer</button>
                  </motion.div>
                )}

                {step === 3 && (
                  <motion.div key="result" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }} data-testid="quiz-result">
                    <span className="crush-badge">Notre recommandation</span>
                    <h2 className="title-xl" style={{ fontSize: "clamp(28px,6vw,44px)", margin: "18px 0 16px" }}>{result.title}</h2>
                    <p style={{ fontSize: 18, color: "var(--text-muted)", marginBottom: 24 }}>{result.text}</p>
                    <img src={(getProduct(result.productId) || CART_EXTRA[result.productId]).image} alt="" loading="lazy" style={{ width: "100%", maxHeight: 300, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)", marginBottom: 24 }} />
                    <button className="btn btn-primary" style={{ width: "100%" }} data-testid="quiz-add" onClick={addRecommended}>{result.cta}</button>
                    <p style={{ color: "var(--text-muted)", fontSize: 13, marginTop: 16, textAlign: "center" }}>★ 4,6/5 · 225 avis vérifiés · Satisfait ou remboursé 30 jours</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
